import { formatRelativeDate } from '@/utils/format';
import { Badge } from '@/components/common';
import type { ActivityLog } from '@/types';

interface ActivityFeedProps {
  activities: ActivityLog[];
  loading?: boolean;
}

export function ActivityFeed({ activities, loading = false }: ActivityFeedProps) {
  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="animate-pulse">
            <div className="flex gap-4">
              <div className="w-10 h-10 bg-secondary-200 rounded-full" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-secondary-200 rounded w-3/4" />
                <div className="h-3 bg-secondary-200 rounded w-1/2" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (activities.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-secondary-500">No recent activity</p>
      </div>
    );
  }

  const getActionBadge = (action: string) => {
    const actionMap: Record<string, { variant: 'success' | 'warning' | 'danger' | 'info' | 'default', label: string }> = {
      'user.login': { variant: 'success', label: 'Login' },
      'user.logout': { variant: 'default', label: 'Logout' },
      'user.created': { variant: 'success', label: 'Created' },
      'user.updated': { variant: 'info', label: 'Updated' },
      'user.deleted': { variant: 'danger', label: 'Deleted' },
      'oauth.authorized': { variant: 'success', label: 'Authorized' },
      'oauth.revoked': { variant: 'warning', label: 'Revoked' },
      'role.assigned': { variant: 'info', label: 'Role Assigned' },
      'policy.created': { variant: 'success', label: 'Policy Created' },
    };

    const config = actionMap[action] || { variant: 'default' as const, label: action };
    return <Badge variant={config.variant} size="sm">{config.label}</Badge>;
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="space-y-4">
      {activities.map((activity) => (
        <div key={activity.id} className="flex gap-4 pb-4 border-b border-secondary-100 last:border-0">
          {/* User Avatar */}
          <div className="flex-shrink-0">
            {activity.user?.avatar ? (
              <img
                src={activity.user.avatar}
                alt={activity.user.name}
                className="w-10 h-10 rounded-full"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-sm font-medium">
                {activity.user ? getInitials(activity.user.name) : '?'}
              </div>
            )}
          </div>

          {/* Activity Details */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1">
                <p className="text-sm text-secondary-900">
                  <span className="font-medium">{activity.user?.name || 'Unknown User'}</span>
                  {' '}
                  <span className="text-secondary-600">
                    performed action on{' '}
                    {activity.resource ? (
                      <span className="font-medium text-secondary-900">{activity.resource}</span>
                    ) : (
                      'system'
                    )}
                  </span>
                </p>
                <p className="text-xs text-secondary-500 mt-1">
                  {formatRelativeDate(activity.created_at)}
                  {activity.ip_address && (
                    <span className="ml-2">• {activity.ip_address}</span>
                  )}
                </p>
              </div>
              {getActionBadge(activity.action)}
            </div>

            {/* Metadata */}
            {activity.metadata && Object.keys(activity.metadata).length > 0 && (
              <div className="mt-2 text-xs text-secondary-600 bg-secondary-50 rounded px-2 py-1">
                {Object.entries(activity.metadata).slice(0, 2).map(([key, value]) => (
                  <div key={key}>
                    <span className="font-medium">{key}:</span> {String(value)}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
